import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Link, useMatchRoute } from '@repo/app-router';
import { ORG_SELECTOR_DROPDOWN_CONTENT_ID, ORG_SWITCHER_DROPDOWN_CONTENT_ID } from '@repo/constants';
import { useOrganizationByName } from '@repo/domains-organization';
import { organization_people_path, organization_settings_path, organization_subscription_path, RouteNames, useTypedParams } from '@repo/routes';
import { cn, generateOrgColor, getInitials } from '@repo/ui-primitives';
import { Avatar, AvatarFallback } from '@repo/ui-primitives/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@repo/ui-primitives/dropdown-menu';
import { Skeleton } from '@repo/ui-primitives/skeleton';
import { useCurrentUserOrganizationList, useUserCanManageOrg } from '@repo/user-context';
import { ChevronDownIcon, CreditCardIcon, SettingsIcon, UsersIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { OrgSwitcher } from './sidebar';
import { OrgSwitcherOptions } from './sidebar/org-switcher-options';
export function OrgSelectorDropdown() {
    var _ref, _ref1, _ref2;
    var _orgData_subscription;
    const { organization } = useTypedParams();
    const matchRoute = useMatchRoute();
    const { organization: orgData, isLoading: isLoadingOrg } = useOrganizationByName(organization);
    const { can: canOrg } = useUserCanManageOrg({
        organization: orgData
    });
    const { organizationList } = useCurrentUserOrganizationList();
    const [selectedOrg, setSelectedOrg] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isOrgSwitcherOpen, setIsOrgSwitcherOpen] = useState(false);
    // Initialize selectedOrg based on current route organization
    useEffect(()=>{
        if (organization && (!selectedOrg || selectedOrg !== organization)) {
            setSelectedOrg(organization);
        }
    }, [
        organization,
        selectedOrg
    ]);
    useEffect(()=>{
        if (!isOpen) {
            return;
        }
        const handleScroll = (event)=>{
            const orgSelectorDropdown = document.getElementById(ORG_SELECTOR_DROPDOWN_CONTENT_ID);
            const orgSwitcherDropdown = document.getElementById(ORG_SWITCHER_DROPDOWN_CONTENT_ID);
            if (!orgSelectorDropdown) {
                setIsOpen(false);
                return;
            }
            const isWithinSelector = isEventWithinDropdown(event, orgSelectorDropdown);
            const isWithinSwitcher = orgSwitcherDropdown ? isEventWithinDropdown(event, orgSwitcherDropdown) : false;
            if (isWithinSelector || isWithinSwitcher) {
                return;
            }
            setIsOpen(false);
        };
        const isEventWithinDropdown = (event, element)=>{
            const target = event.target;
            if (target && element.contains(target)) {
                return true;
            }
            return event.composedPath().some((node)=>node === element);
        };
        window.addEventListener('scroll', handleScroll, true);
        return ()=>{
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [
        isOpen
    ]);
    const isOrganizationRoot = useMemo(()=>{
        if (!organization) {
            return false;
        }
        return !!matchRoute({
            to: RouteNames.ORGANIZATION,
            params: {
                organization
            },
            // exact match
            fuzzy: false
        });
    }, [
        matchRoute,
        organization
    ]);
    if (!organizationList) {
        return null;
    }
    // Generate consistent colors and initials for the organization trigger
    const orgDisplayName = (_ref = (_ref1 = orgData === null || orgData === void 0 ? void 0 : orgData.displayName) !== null && _ref1 !== void 0 ? _ref1 : organization) !== null && _ref !== void 0 ? _ref : 'Select Organization';
    const orgSubscriptionTier = (_ref2 = orgData === null || orgData === void 0 ? void 0 : (_orgData_subscription = orgData.subscription) === null || _orgData_subscription === void 0 ? void 0 : _orgData_subscription.tier) !== null && _ref2 !== void 0 ? _ref2 : 'PERSONAL';
    const avatarColor = generateOrgColor(orgDisplayName);
    // TODO: org avatar color and initials are not normalized between popups
    const orgInitials = getInitials(orgDisplayName);
    const onChangeOrgHandler = (orgName)=>{
        setSelectedOrg(orgName);
        setIsOpen(false);
    };
    const skeletonTriggerContent = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Skeleton, {
                className: "w-8 h-8 rounded-md"
            }),
            /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-5 w-32"
            })
        ]
    });
    const triggerContent = isLoadingOrg ? skeletonTriggerContent : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Avatar, {
                className: "w-8 h-8 rounded-md",
                children: /*#__PURE__*/ _jsx(AvatarFallback, {
                    className: cn("text-xs font-medium rounded-md bg-[".concat(avatarColor, "]  text-white")),
                    style: {
                        backgroundColor: avatarColor
                    },
                    children: orgInitials
                })
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "font-medium text-foreground",
                children: orgDisplayName
            })
        ]
    });
    const dropdownContent = /*#__PURE__*/ _jsx(DropdownMenuContent, {
        id: ORG_SELECTOR_DROPDOWN_CONTENT_ID,
        side: "bottom",
        align: "start",
        sideOffset: 8,
        className: "w-[--radix-dropdown-menu-trigger-width] min-w-[240px] text-gray-25 border-border ".concat(isOrgSwitcherOpen ? 'rounded-tr-none rounded-br-none' : ''),
        children: organization ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(OrgSwitcher, {
                    selectedOrg: selectedOrg,
                    organizations: organizationList,
                    onOrgChange: onChangeOrgHandler,
                    isOpen: isOrgSwitcherOpen,
                    onOpen: (value)=>{
                        setIsOrgSwitcherOpen(value);
                    },
                    onClose: ()=>{
                        setIsOpen(false);
                    },
                    orgSubscriptionTier: orgSubscriptionTier
                }),
                /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                /*#__PURE__*/ _jsxs(DropdownMenuGroup, {
                    children: [
                        /*#__PURE__*/ _jsx(DropdownMenuLabel, {
                            children: "Organization"
                        }),
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            asChild: true,
                            disabled: !(canOrg === null || canOrg === void 0 ? void 0 : canOrg.updateOrganizationSettings),
                            children: /*#__PURE__*/ _jsxs(Link, {
                                to: organization_settings_path({
                                    organization: {
                                        name: organization
                                    }
                                }),
                                className: "flex items-center gap-2 cursor-pointer",
                                children: [
                                    /*#__PURE__*/ _jsx(SettingsIcon, {
                                        className: "w-4 h-4"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: "Settings"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            asChild: true,
                            disabled: !(canOrg === null || canOrg === void 0 ? void 0 : canOrg.updateOrganizationSettings),
                            children: /*#__PURE__*/ _jsxs(Link, {
                                to: organization_subscription_path({
                                    organization: {
                                        name: organization
                                    }
                                }),
                                className: "flex items-center gap-2 cursor-pointer",
                                children: [
                                    /*#__PURE__*/ _jsx(CreditCardIcon, {
                                        className: "w-4 h-4"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: "Billing"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            asChild: true,
                            disabled: !(canOrg === null || canOrg === void 0 ? void 0 : canOrg.updateOrganizationSettings),
                            children: /*#__PURE__*/ _jsxs(Link, {
                                to: organization_people_path({
                                    organization: {
                                        name: organization
                                    }
                                }),
                                className: "flex items-center gap-2 cursor-pointer",
                                children: [
                                    /*#__PURE__*/ _jsx(UsersIcon, {
                                        className: "w-4 h-4"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: "Manage users"
                                    })
                                ]
                            })
                        })
                    ]
                })
            ]
        }) : /*#__PURE__*/ _jsx(OrgSwitcherOptions, {
            organizations: organizationList,
            onOrgChange: setSelectedOrg,
            onClose: ()=>{
                setIsOpen(false);
            }
        })
    });
    if (organization && isOrganizationRoot) {
        return /*#__PURE__*/ _jsxs(DropdownMenu, {
            open: isOpen,
            onOpenChange: setIsOpen,
            children: [
                /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                    asChild: true,
                    children: /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        className: "flex items-center gap-2 m-0 p-0 relative z-50 cursor-pointer",
                        "aria-haspopup": "menu",
                        "aria-expanded": isOpen,
                        children: [
                            triggerContent,
                            /*#__PURE__*/ _jsx(ChevronDownIcon, {
                                id: "org-selector-dropdown-trigger",
                                className: "w-5 h-5 text-muted-foreground ring-1 ring-muted-foreground/50 rounded-sm"
                            })
                        ]
                    })
                }),
                dropdownContent
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-2",
        children: [
            organization ? /*#__PURE__*/ _jsx(Link, {
                to: "/org/$organization",
                params: {
                    organization
                },
                className: "flex items-center gap-2",
                children: triggerContent
            }) : /*#__PURE__*/ _jsx("div", {
                className: "flex items-center gap-2",
                children: triggerContent
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex items-center m-0 p-0 relative z-50",
                children: /*#__PURE__*/ _jsxs(DropdownMenu, {
                    open: isOpen,
                    onOpenChange: setIsOpen,
                    children: [
                        /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                            children: /*#__PURE__*/ _jsx(ChevronDownIcon, {
                                id: "org-selector-dropdown-trigger",
                                className: "w-5 h-5 text-muted-foreground ring-1 ring-muted-foreground/50 rounded-sm"
                            })
                        }),
                        dropdownContent
                    ]
                })
            })
        ]
    });
}
